// 👇 notice where this is imported from!
import { createBrowserClient } from '@supabase/ssr'

import { createAuth } from '@cedarjs/auth-supabase-web'
import type { CurrentUser } from '@cedarjs/auth';
import { useCallback } from "react";
import { useConfigurationStore } from 'src/lib/store';

const supabaseClient = createBrowserClient(
  process.env.SUPABASE_URL || '',
  process.env.SUPABASE_KEY || ''
)

const useHasRole = (currentUser: CurrentUser | null) => {
  // Subscribe to role override changes to trigger re-renders
  const roleOverride = useConfigurationStore(state => state.roleOverride);
  
  /**
   * @example
   *
   * Checks if the "currentUser" from the api side
   * is assigned a role or one of a list of roles.
   * If the user is assigned any of the provided list of roles,
   * the hasRole is considered to be true.
   */
  return useCallback(
    function hasModelPermission(rolesToCheck: string | string[] ): boolean {
      let permissions = Array.isArray(rolesToCheck) ? rolesToCheck[0] : rolesToCheck;
      
      let [resource, operation, idSite] = permissions?.split(".");
      //By default, operation are read if unspecified
      if(!operation)
        operation = "read"
      if (!currentUser) {
        return false
      }

      // When role override is active for super admin, use ONLY override roles (no super admin privileges)
      if (currentUser.isSuperAdmin && roleOverride.selectedRoles.length > 0) {
        // Check if any of the selected override roles have the required permission
        for (const selectedRole of roleOverride.selectedRoles) {
          // Check if this role has permissions data
          if (selectedRole.permissions) {
            for (const permission of selectedRole.permissions) {
              // Check if this permission matches the resource and site
              if (permission.resource === resource) {
                // If site is specified, check if permission applies to this site
                if (idSite && selectedRole.idSite !== parseInt(idSite)) {
                  continue;
                }
                
                // Check the specific operation
                switch (operation) {
                  case 'read':
                    if (permission.read) return true;
                    break;
                  case 'create':
                    if (permission.create) return true;
                    break;
                  case 'update':
                    if (permission.update) return true;
                    break;
                  case 'delete':
                    if (permission.delete) return true;
                    break;
                }
              }
            }
          }
        }
        return false; // No matching permission found in override roles
      }

      // Super admin has all permissions when not overridden
      if (currentUser.isSuperAdmin && roleOverride.selectedRoles.length === 0) {
        return true
      }

      // Check site-specific permissions for regular users
      if (currentUser.roles) {
        //Will latch the latest true value
        let _hasRight = false;
        for (const profileRole of currentUser.roles) {
          // If site is specified, check if role applies to this site
          if (idSite && profileRole.idSite !== parseInt(idSite)) {
            continue
          }

          const permission = profileRole.permissions.find(p => p.resource === resource)
          if (permission) {
            switch (operation) {
              case 'read':
                _hasRight = _hasRight || permission.read;
                break;
              case 'create':
                _hasRight = _hasRight || permission.create;
                break;
              case 'update':
                _hasRight = _hasRight || permission.update;
                break;
              case 'delete':
                _hasRight = _hasRight || permission.delete;
                break;
            }
          }
        }
        return _hasRight
      }

      return false
    },
    [currentUser, roleOverride],
  )
}

/*
(rolesToCheck: string | string[]): boolean => {
  if (currentUser?.roles) {
    if (typeof rolesToCheck === 'string') {
      if (typeof currentUser.roles === 'string') {
        // rolesToCheck is a string, currentUser.roles is a string
        return currentUser.roles === rolesToCheck
      } else if (Array.isArray(currentUser.roles)) {
        // rolesToCheck is a string, currentUser.roles is an array
        return currentUser.roles?.some(
          (allowedRole) => rolesToCheck === allowedRole,
        )
      }
    }

    if (Array.isArray(rolesToCheck)) {
      if (Array.isArray(currentUser.roles)) {
        // rolesToCheck is an array, currentUser.roles is an array
        return currentUser.roles?.some((allowedRole) =>
          rolesToCheck.includes(allowedRole),
        )
      } else if (typeof currentUser.roles === 'string') {
        // rolesToCheck is an array, currentUser.roles is a string
        return rolesToCheck.some(
          (allowedRole) => currentUser?.roles === allowedRole,
        )
      }
    }
  }

  return false
},
*/
export const { AuthProvider, useAuth } = createAuth(supabaseClient, {
  useHasRole: useHasRole,
})